/**
 * True when this row is the tree root / “Start/Top of Line” person (generation 1),
 * matching the dashboard line column label and family-tree conventions.
 *
 * Dashboard list rows use `head_first_name` for the colored “head of line” name; when it is
 * absent, the UI shows “Start/Top of Line”. Statistics payloads often omit `generation`, so we
 * also treat missing generation + missing head as that root row.
 */
/**
 * Start/Top of Line by positive evidence only — an explicit generation 1 or a
 * "self" relation. Unlike {@link isDashboardStartOrTopOfLineMember} this never
 * infers the root from missing fields, so it is the safe test on payloads that
 * simply do not ship `head_first_name` / `generation` (the `/dashboard` Upcoming
 * lists, whose line column is resolved from the heads map by colour instead).
 */
export function isExplicitDashboardStartOrTopOfLineMember(member) {
  if (!member || typeof member !== "object") return false;

  const rawGen = member.generation ?? member.generation_level;
  const genStr =
    rawGen !== undefined && rawGen !== null ? String(rawGen).trim() : "";
  if (genStr !== "" && Number(genStr) === 1) return true;

  const rel = String(
    member.relation_to_head_of_line ||
      member.relation_to_top_of_line ||
      member.relation ||
      ""
  )
    .trim()
    .toLowerCase();

  return (
    rel === "self" ||
    rel.includes("self (top of line)") ||
    rel.includes("self head of line")
  );
}

export function isDashboardStartOrTopOfLineMember(member) {
  if (!member || typeof member !== "object") return false;

  const rawGen = member.generation ?? member.generation_level;
  const genStr =
    rawGen !== undefined && rawGen !== null ? String(rawGen).trim() : "";
  const genMissing = genStr === "";
  const genNum = genMissing ? NaN : Number(genStr);

  if (!genMissing && !Number.isNaN(genNum) && genNum === 1) return true;

  const rel = String(
    member.relation_to_head_of_line ||
      member.relation_to_top_of_line ||
      member.relation ||
      ""
  )
    .trim()
    .toLowerCase();

  if (rel === "self") return true;
  if (rel.includes("self (top of line)") || rel.includes("self head of line")) {
    return true;
  }

  const headFirst = String(member.head_first_name || "").trim();
  if (!headFirst) {
    if (genMissing) return true;
    if (!Number.isNaN(genNum) && genNum <= 1) return true;
  }

  return false;
}
